import { useMemo } from "react";
import { ShowcaseArtworkCard } from "./ArtworkCard";

function getArtworkKey(a) {
  const id = a?.id ?? a?.artwork_id ?? a?.artworkId;
  if (id != null) return `art-${id}`;

  const img = a?.image_url ?? a?.imageUrl ?? "";
  const title = a?.title ?? "";
  return `art-fallback-${title}-${img}`;
}

function getArtworkId(a) {
  return a?.id ?? a?.artwork_id ?? a?.artworkId ?? null;
}

// Try to read dimensions from whatever your API might provide
function getArtworkDims(a) {
  const w =
    a?.image_width ??
    a?.imageWidth ??
    a?.width ??
    a?.w ??
    a?.meta?.width ??
    a?.media?.width ??
    null;

  const h =
    a?.image_height ??
    a?.imageHeight ??
    a?.height ??
    a?.h ??
    a?.meta?.height ??
    a?.media?.height ??
    null;

  const W = Number(w);
  const H = Number(h);

  if (!Number.isFinite(W) || !Number.isFinite(H) || W <= 0 || H <= 0) {
    return null;
  }
  return { w: W, h: H, ar: W / H };
}

function clampInt(n, min, max, fallback) {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  return Math.max(min, Math.min(max, Math.floor(x)));
}

export default function ArtGrid({
  artworks = [],
  dense = false,

  // OPTIONAL: featured artwork IDs (NOT automatic)
  featuredIds = [],

  // Optional: parent can still override columns, but we hard-cap to 3
  columns,
  gridClassName = "",

  title,
  subtitle,
  right,
  emptyCta,

  noWrapper = false,

  // reduce how many cards render (curated feel)
  limit = dense ? 12 : 9,

  // prioritize featured inside the limit
  prioritizeFeatured = true,

  // pass-through for ShowcaseArtworkCard navigation/state
  hubState,

  // NEW: enable “editorial” varying tiles (bigger/smaller) when not dense
  editorial = !dense,

  // NEW: caps for how “large” tiles can get
  maxColSpan = 2, // never go beyond 2 (safe even with 3-col cap)
  maxRowSpan = 2, // never go beyond 2 (keeps tiles from getting huge)
}) {
  const itemsRaw = useMemo(
    () => (Array.isArray(artworks) ? artworks : []),
    [artworks]
  );
  const hasArt = itemsRaw.length > 0;

  const featuredSet = useMemo(
    () => new Set((featuredIds || []).map((x) => String(x))),
    [featuredIds]
  );

  const items = useMemo(() => {
    const arr = itemsRaw.slice();
    if (!arr.length) return arr;

    const isFeat = (a) => {
      const id = getArtworkId(a);
      return id != null && featuredSet.has(String(id));
    };

    const ordered = prioritizeFeatured
      ? [...arr.filter(isFeat), ...arr.filter((a) => !isFeat(a))]
      : arr;

    if (limit == null) return ordered;
    const n = Math.max(0, Number(limit) || 0);
    return ordered.slice(0, n);
  }, [itemsRaw, featuredSet, limit, prioritizeFeatured]);

  const hasShown = items.length > 0;

  const gap = dense ? "gap-5" : "gap-7";
  const padding = dense ? "p-4 sm:p-5" : "p-5 sm:p-6";

  // Hard-cap to max 3 columns using static Tailwind classes
  const resolvedCols = columns || { base: 1, sm: 2, lg: 3 };

  const base = clampInt(resolvedCols.base, 1, 3, 1);
  const sm = clampInt(resolvedCols.sm, 1, 3, 2);
  const lg = clampInt(resolvedCols.lg, 1, 3, 3);

  const colsClass =
    gridClassName ||
    [
      "grid",
      base === 1 ? "grid-cols-1" : base === 2 ? "grid-cols-2" : "grid-cols-3",
      sm === 1 ? "sm:grid-cols-1" : sm === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3",
      lg === 1 ? "lg:grid-cols-1" : lg === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3",
      "items-stretch",
    ].join(" ");

  const Wrapper = ({ children }) =>
    noWrapper ? (
      <>{children}</>
    ) : (
      <div
        className={[
          "rounded-[28px] overflow-hidden",
          "border border-white/10",
          "bg-transparent",
          "backdrop-blur-md",
          "shadow-[0_18px_80px_rgba(0,0,0,0.42)]",
        ].join(" ")}
      >
        <div className={padding}>{children}</div>
      </div>
    );

  // Decide whether a tile should be larger/smaller based on aspect ratio
  // - landscape gets wider (col-span-2)
  // - portrait gets taller (row-span-2)
  // - square stays normal
  function getTileShape(a) {
    if (!editorial || dense) return { colSpan: 1, rowSpan: 1 };

    // If you explicitly feature something, it already can be wide (handled below).
    // This function is for “organic” sizing.
    const dims = getArtworkDims(a);
    if (!dims) return { colSpan: 1, rowSpan: 1 };

    const { ar } = dims;

    // Tune thresholds to taste:
    const isLandscape = ar >= 1.35;
    const isPortrait = ar <= 0.82;

    if (isLandscape) {
      return { colSpan: Math.min(maxColSpan, 2), rowSpan: 1 };
    }
    if (isPortrait) {
      return { colSpan: 1, rowSpan: Math.min(maxRowSpan, 2) };
    }
    return { colSpan: 1, rowSpan: 1 };
  }

  return (
    <div className="w-full">
      {(title || subtitle || right) && (
        <div className="mb-5 flex items-end justify-between gap-3 flex-wrap">
          <div>
            {title ? (
              <div className="text-xl font-semibold tracking-tight text-white/90">
                {title}
              </div>
            ) : null}
            {subtitle ? (
              <div className="mt-1 text-sm text-white/55">{subtitle}</div>
            ) : null}
          </div>
          {right ? <div className="flex items-center gap-2">{right}</div> : null}
        </div>
      )}

      <Wrapper>
        {!hasArt ? (
          <div className="rounded-[22px] border border-white/10 bg-white/[0.03] backdrop-blur-md p-6">
            <div className="text-sm font-semibold text-white/85">No art found</div>
            <div className="mt-2 text-sm text-white/55">
              Try clearing filters or searching something else.
            </div>
            {emptyCta ? <div className="mt-4">{emptyCta}</div> : null}
          </div>
        ) : !hasShown ? (
          <div className="rounded-[22px] border border-white/10 bg-white/[0.03] backdrop-blur-md p-6">
            <div className="text-sm font-semibold text-white/85">Nothing to display</div>
            <div className="mt-2 text-sm text-white/55">
              Your limit is set to 0. Increase it to show artwork.
            </div>
          </div>
        ) : (
          <div className="mx-auto w-full max-w-[1200px]">
            <div className={`${colsClass} ${gap}`}>
              {items.map((a) => {
                const id = getArtworkId(a);

                // Existing “featured” behavior (only when not dense)
                const isFeatured =
                  id != null && featuredSet.has(String(id)) && !dense;

                // Editorial sizing (image-driven)
                const shape = getTileShape(a);

                // Final spans:
                // - Featured stays wide on lg, but never beyond 2
                // - Otherwise use editorial sizing on lg+
                const colSpanLg = isFeatured ? Math.min(maxColSpan, 2) : shape.colSpan;
                const rowSpanLg = isFeatured ? 1 : shape.rowSpan;

                // Aesthetic + more “gallery” feel: remove the forced tall empty space,
                // and let the card content define height. We keep a minimum so the grid
                // doesn’t collapse when images are small/slow-loading.
                const minH = dense
                  ? "min-h-[320px] sm:min-h-[360px]"
                  : "min-h-[300px] sm:min-h-[340px]";

                return (
                  <div
                    key={getArtworkKey(a)}
                    className={[
                      "min-w-0",
                      "group relative",
                      "rounded-[26px]",
                      "border border-white/10",
                      "bg-white/[0.02]",
                      "backdrop-blur-sm",
                      "shadow-[0_12px_60px_rgba(0,0,0,0.40)]",
                      "overflow-hidden",
                      minH,
                      // subtle “sheen” + depth (no neon/glow)
                      "ring-1 ring-white/[0.06]",
                      "transition-transform duration-200 hover:-translate-y-[2px]",
                      "will-change-transform",
                      // IMPORTANT: spans only apply when >= lg (so mobile/tablet stays clean)
                      colSpanLg === 2 ? "lg:col-span-2" : "lg:col-span-1",
                      rowSpanLg === 2 ? "lg:row-span-2" : "lg:row-span-1",
                    ].join(" ")}
                  >
                    {/* soft overlay for modern depth (very subtle) */}
                    <div className="pointer-events-none absolute inset-0">
                      <div className="absolute inset-0 bg-gradient-to-b from-white/[0.06] via-transparent to-black/[0.20]" />
                      <div className="absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100 bg-white/[0.02]" />
                    </div>

                    <div className="relative h-full">
                      <ShowcaseArtworkCard artwork={a} hubState={hubState} />
                    </div>
                  </div>
                );
              })}
            </div>

            {limit != null && itemsRaw.length > items.length ? (
              <div className="mt-5 text-xs text-white/45">
                Showing {items.length} of {itemsRaw.length}.
              </div>
            ) : null}
          </div>
        )}
      </Wrapper>
    </div>
  );
}
